import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  XCircleIcon, 
  ExclamationCircleIcon 
} from '@heroicons/react/24/outline';

const RecentBookings = ({ bookings, isLoading }) => {
  // Function to render status badge
  const renderStatusBadge = (status) => {
    switch (status.toLowerCase()) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="mr-1 h-4 w-4" />
            Confirmed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <ClockIcon className="mr-1 h-4 w-4" />
            Pending
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircleIcon className="mr-1 h-4 w-4" />
            Cancelled
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ExclamationCircleIcon className="mr-1 h-4 w-4" />
            {status}
          </span>
        );
    }
  };

  // Placeholder data for when API data is not available
  const placeholderBookings = [
    {
      id: 1,
      bookingNumber: 'BK-2023-001',
      customerName: 'John Doe',
      tourName: 'Maasai Mara Safari',
      date: '2023-04-15',
      amount: 1200,
      status: 'confirmed'
    },
    {
      id: 2,
      bookingNumber: 'BK-2023-002',
      customerName: 'Jane Smith',
      tourName: 'Amboseli National Park',
      date: '2023-04-18',
      amount: 950,
      status: 'pending'
    },
    {
      id: 3,
      bookingNumber: 'BK-2023-003',
      customerName: 'Robert Johnson',
      tourName: 'Lake Nakuru Tour',
      date: '2023-04-20',
      amount: 750,
      status: 'cancelled'
    },
    {
      id: 4,
      bookingNumber: 'BK-2023-004',
      customerName: 'Emily Williams',
      tourName: 'Mount Kenya Trekking',
      date: '2023-04-25',
      amount: 1500,
      status: 'confirmed'
    },
    {
      id: 5,
      bookingNumber: 'BK-2023-005',
      customerName: 'Michael Brown',
      tourName: 'Diani Beach Getaway',
      date: '2023-04-30',
      amount: 850,
      status: 'pending'
    }
  ];

  // Use placeholder data if no bookings are provided or if loading
  const displayBookings = bookings.length > 0 ? bookings : placeholderBookings;

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <div className="h-10 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded mb-4"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Booking
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Customer
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayBookings.map((booking) => (
              <tr key={booking.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <Link to={`/admin/bookings/${booking.id}`} className="text-primary hover:text-primary-dark">
                    {booking.bookingNumber}
                  </Link>
                  <div className="text-sm text-gray-500">{booking.tourName}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{booking.customerName}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {new Date(booking.date).toLocaleDateString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    ${booking.amount.toLocaleString()}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {renderStatusBadge(booking.status)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6">
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-700">
            Showing <span className="font-medium">5</span> of <span className="font-medium">20</span> bookings
          </div>
          <Link to="/admin/bookings" className="text-sm font-medium text-primary hover:text-primary-dark">
            View all bookings
          </Link>
        </div>
      </div>
    </div>
  );
};

RecentBookings.propTypes = {
  bookings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      bookingNumber: PropTypes.string.isRequired,
      customerName: PropTypes.string.isRequired,
      tourName: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
      status: PropTypes.string.isRequired
    })
  ),
  isLoading: PropTypes.bool
};

RecentBookings.defaultProps = {
  bookings: [],
  isLoading: false
};

export default RecentBookings;
